import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag, X, Truck, Wallet, RefreshCw, Gift } from "lucide-react";
import { toast } from "sonner";
import { formatINR, useCart } from "../context/CartContext";
import SafeImg from "../components/SafeImg";

export default function Cart() {
  const { items, updateQuantity, removeItem, totals, coupon, applyCoupon, removeCoupon } = useCart();
  const [couponCode, setCouponCode] = useState("");
  const [applying, setApplying] = useState(false);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;
    setApplying(true);
    const r = await applyCoupon(couponCode.trim());
    setApplying(false);
    if (r.ok) {
      toast.success(`${r.code} applied`, { description: r.message });
      setCouponCode("");
    } else {
      toast.error(r.error);
    }
  };

  if (items.length === 0) {
    return (
      <div
        data-testid="cart-empty"
        className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center"
      >
        <div className="h-20 w-20 rounded-full bg-blush-100 grid place-items-center text-blush-600 mx-auto">
          <ShoppingBag size={22} strokeWidth={1.4} />
        </div>
        <span className="mt-6 block font-script text-3xl text-blush-600">
          softly empty
        </span>
        <h1 className="font-heading text-4xl md:text-5xl text-ink-900 mt-2">
          Your cart is waiting
        </h1>
        <p className="mt-4 font-body text-ink-700">
          Let's fill it with pieces that move with you.
        </p>
        <Link to="/shop" className="btn-maroon mt-8 inline-flex items-center gap-2">
          Explore kurtis <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pb-24">
      <section className="relative py-16 md:py-20 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 border-b border-blush-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-3xl text-blush-400">
            your selection
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-ink-900 mt-1">
            Shopping Cart
          </h1>
          <p className="mt-3 font-body text-ink-700">
            {totals.count} {totals.count === 1 ? "piece" : "pieces"} softly set aside for you.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-4">
          {items.map((item) => (
            <article
              key={item.key}
              data-testid={`cart-item-${item.key}`}
              className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr_auto] gap-5 items-center bg-white border border-blush-100 rounded-3xl p-4 md:p-5"
            >
              <Link to={`/product/${item.id}`} className="block">
                <SafeImg
                  src={item.image}
                  alt={item.name}
                  className="aspect-[4/5] w-full object-cover rounded-2xl"
                />
              </Link>

              <div className="min-w-0">
                <Link to={`/product/${item.id}`}>
                  <h3 className="font-heading text-xl text-ink-900 truncate">
                    {item.name}
                  </h3>
                </Link>
                <p className="mt-1 font-body text-sm text-ink-500">
                  Size {item.size}
                </p>
                <p className="mt-2 font-heading text-lg text-blush-600">
                  {formatINR(item.price)}
                </p>

                <div className="mt-4 flex items-center gap-3 md:hidden">
                  <div className="flex items-center gap-1 px-2 h-10 rounded-full border border-blush-100">
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      className="h-7 w-7 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
                    >
                      <Minus size={12} strokeWidth={1.5} />
                    </button>
                    <span className="w-6 text-center text-sm font-body">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="h-7 w-7 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
                    >
                      <Plus size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.key)}
                    className="h-10 w-10 grid place-items-center rounded-full border border-blush-100 text-blush-600 hover:bg-blush-50"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-4">
                <div className="flex items-center gap-1 px-2 h-11 rounded-full border border-blush-100">
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity - 1)}
                    data-testid={`cart-dec-${item.key}`}
                    className="h-8 w-8 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
                  >
                    <Minus size={13} strokeWidth={1.5} />
                  </button>
                  <span
                    className="w-6 text-center font-body text-ink-900"
                    data-testid={`cart-qty-${item.key}`}
                  >
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.key, item.quantity + 1)}
                    data-testid={`cart-inc-${item.key}`}
                    className="h-8 w-8 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
                  >
                    <Plus size={13} strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  onClick={() => removeItem(item.key)}
                  data-testid={`cart-remove-${item.key}`}
                  className="h-11 w-11 grid place-items-center rounded-full border border-blush-100 text-blush-600 hover:bg-blush-50 transition"
                  aria-label="Remove"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </article>
          ))}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 rounded-3xl bg-white border border-blush-100 p-6 md:p-8 shadow-sm">
            <FreeGiftProgress subtotal={totals.subtotal} />
            <h3 className="font-heading text-2xl text-ink-900">Order summary</h3>
            <dl className="mt-6 space-y-3 font-body text-ink-700">
              <div className="flex items-center justify-between">
                <dt>Subtotal</dt>
                <dd data-testid="cart-subtotal">{formatINR(totals.subtotal)}</dd>
              </div>
              {totals.discount > 0 && (
                <div className="flex items-center justify-between text-blush-600">
                  <dt className="inline-flex items-center gap-1">
                    <Tag size={12} strokeWidth={1.6} />
                    {coupon?.code}
                    <button
                      onClick={removeCoupon}
                      data-testid="cart-coupon-remove"
                      aria-label="Remove coupon"
                      className="ml-1 h-4 w-4 grid place-items-center rounded-full hover:bg-blush-50"
                    >
                      <X size={10} strokeWidth={2} />
                    </button>
                  </dt>
                  <dd data-testid="cart-discount">- {formatINR(totals.discount)}</dd>
                </div>
              )}
              <div className="flex items-center justify-between">
                <dt>Shipping</dt>
                <dd data-testid="cart-shipping">
                  {totals.shipping === 0 ? "Free" : formatINR(totals.shipping)}
                </dd>
              </div>
              {totals.tax > 0 && (
                <div className="flex items-center justify-between">
                  <dt>Tax (estimated)</dt>
                  <dd data-testid="cart-tax">{formatINR(totals.tax)}</dd>
                </div>
              )}
              <div className="h-px bg-blush-100 my-3" />
              <div className="flex items-center justify-between text-lg">
                <dt className="font-heading text-ink-900">Total</dt>
                <dd
                  data-testid="cart-total"
                  className="font-heading text-ink-900"
                >
                  {formatINR(totals.total)}
                </dd>
              </div>
            </dl>

            {!coupon && (
              <form onSubmit={handleApply} className="mt-6 flex items-stretch gap-2" data-testid="cart-coupon-form">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Coupon code"
                  data-testid="cart-coupon-input"
                  className="field flex-1 uppercase tracking-wider text-sm"
                />
                <button
                  type="submit"
                  disabled={applying}
                  data-testid="cart-coupon-apply"
                  className="btn-ghost-rose text-xs px-5"
                >
                  {applying ? "…" : "Apply"}
                </button>
              </form>
            )}

            <Link
              to="/checkout"
              data-testid="cart-checkout-btn"
              className="btn-maroon mt-8 w-full inline-flex items-center justify-center gap-2"
            >
              Proceed to checkout <ArrowRight size={14} strokeWidth={1.5} />
            </Link>

            <Link
              to="/shop"
              className="mt-3 block text-center text-sm font-body text-ink-700 hover:text-blush-600"
            >
              Continue shopping
            </Link>

            <p className="mt-6 text-xs font-body text-ink-500 leading-relaxed">
              Easy 7-day returns on unworn pieces.
            </p>

            {/* Trust strip */}
            <div className="mt-5 grid grid-cols-3 gap-2 rounded-2xl bg-blush-50/60 border border-blush-100 p-3" data-testid="cart-trust-strip">
              <TrustBadge icon={Truck} label="Free shipping" sub="Pan-India" />
              <TrustBadge icon={Wallet} label="COD available" sub="On all orders" />
              <TrustBadge icon={RefreshCw} label="Easy returns" sub="7 days" />
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function TrustBadge({ icon: Icon, label, sub }) {
  return (
    <div className="flex flex-col items-center text-center gap-1 px-1 py-2">
      <Icon size={16} strokeWidth={1.6} className="text-blush-600" />
      <p className="text-[10px] sm:text-[11px] font-body font-medium text-ink-900 leading-tight">{label}</p>
      <p className="text-[9px] sm:text-[10px] font-body text-ink-500 leading-tight">{sub}</p>
    </div>
  );
}

const FREE_GIFT_THRESHOLD = 2500;

function FreeGiftProgress({ subtotal }) {
  const unlocked = subtotal >= FREE_GIFT_THRESHOLD;
  const pct = Math.min(100, Math.round((subtotal / FREE_GIFT_THRESHOLD) * 100));
  const remaining = Math.max(0, FREE_GIFT_THRESHOLD - subtotal);
  return (
    <div
      data-testid="free-gift-progress"
      className={`mb-6 rounded-2xl p-4 border ${unlocked ? "bg-gradient-to-br from-blush-50 to-blush-100 border-blush-300" : "bg-cream-100 border-blush-100"}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${unlocked ? "bg-blush-600 text-white" : "bg-white text-blush-600 border border-blush-200"}`}>
          <Gift size={16} strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          {unlocked ? (
            <>
              <p className="font-heading text-sm text-ink-900">You've unlocked a free gift ✨</p>
              <p className="text-[11px] font-body text-ink-700 mt-0.5">
                A Flownté surprise will arrive with your order.
              </p>
            </>
          ) : (
            <>
              <p className="font-body text-xs text-ink-700">
                Add <span className="font-medium text-blush-700">₹{remaining.toLocaleString("en-IN")}</span> more to unlock a{" "}
                <span className="font-medium text-ink-900">free gift</span>
              </p>
            </>
          )}
          <div className="mt-2 h-1.5 rounded-full bg-blush-100 overflow-hidden">
            <div
              className="h-full bg-blush-600 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
